import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
  },
  // Organization Header Styles
  orgHeader: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#3B0069",
    paddingBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  orgInfo: {
    flexDirection: "column",
    maxWidth: "60%",
    alignItems: "flex-end", // Align text to the right
  },
  logoContainer: {
    width: 80,
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  orgName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#3B0069",
    marginBottom: 4,
    textAlign: "right",
  },
  orgText: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
    textAlign: "right",
  },
  // Document Header (Title etc)
  docHeader: {
    marginBottom: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  docTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#3B0069",
    // textTransform: "uppercase",
  },
  docMeta: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  metaText: {
    fontSize: 10,
    color: "#666666",
    marginBottom: 2,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    backgroundColor: "#F3F4F6",
    padding: 5,
    color: "#3B0069",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    paddingVertical: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: "#EEEEEE",
  },
  label: {
    fontSize: 10,
    color: "#666666",
  },
  value: {
    fontSize: 10,
    textAlign: "right",
    color: "#333333",
    fontWeight: "medium",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#3B0069",
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#3B0069",
  },
  totalValue: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "right",
    color: "#3B0069",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 10,
    fontSize: 8,
    color: "#999999",
    textAlign: "center",
  },
  materialsTable: {
    marginTop: 5,
  },
  itemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 0.5,
    borderBottomColor: "#EEEEEE",
    paddingVertical: 6,
    alignItems: "center",
  },
  tableHeader: {
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  // Specific column widths for MR
  colMaterial: { width: "35%", fontSize: 9 },
  colCategory: { width: "20%", fontSize: 9, textAlign: "center" },
  colQty: { width: "15%", fontSize: 9, textAlign: "center" },
  colUom: { width: "10%", fontSize: 9, textAlign: "center" },
  // For PO/Quotation
  colName: { width: "35%", fontSize: 9 },
  colNormal: { width: "15%", fontSize: 9, textAlign: "center" },
  colRight: { width: "20%", fontSize: 9, textAlign: "right" },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  summaryLabel: {
    fontSize: 10,
    color: "#666666",
  },
  summaryValue: {
    fontSize: 10,
    textAlign: "right",
    color: "#333333",
  },
});

const getOrgDetails = () => {
  try {
    const org = localStorage.getItem("organization");
    if (org) return JSON.parse(org);
  } catch (e) {
    console.error("Error parsing organization details", e);
  }
  return null;
};

const OrgHeader = () => {
  const org = getOrgDetails();
  if (!org) return null;

  return (
    <View style={styles.orgHeader}>
      <View style={styles.logoContainer}>
        {org.logo_image_file ? (
          <Image src={org.logo_image_file} style={styles.logo} />
        ) : (
          <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#EEE' }}>LOGO</Text>
        )}
      </View>
      <View style={styles.orgInfo}>
        <Text style={styles.orgName}>{org.organization_name}</Text>
        <Text style={styles.orgText}>{org.business_address}</Text>
        <Text style={styles.orgText}>
          {org.city ? `${org.city} ` : ""}{org.state} - {org.pincode}
        </Text>
        <Text style={styles.orgText}>Ph: {org.contact_number}</Text>
        <Text style={styles.orgText}>{org.user_email}</Text>
        {org.website && <Text style={styles.orgText}>{org.website}</Text>}
      </View>
    </View>
  );
};

export const MaterialRequestReceiptPDF = ({
  request,
  warehouseMaterials = [],
}) => {
  const formatUom = (rawUom) => {
    if (!rawUom) return "";
    const u = String(rawUom).trim().toLowerCase();
    if (u.startsWith("kilogram")) return "kgs";
    if (u === "kg" || u === "kgs") return "kgs";
    return rawUom;
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const safeLines = Array.isArray(request?.material_request_lines)
    ? request.material_request_lines
    : [];

  const getWarehouseQty = (line) => {
    const match = warehouseMaterials.find(
      (m) => m?.material?.id === line.material
    );
    return match?.current_quantity ?? 0;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <OrgHeader />

        {/* Document Header */}
        <View style={styles.docHeader}>
          <View>
            <Text style={styles.docTitle}>Material Request</Text>
          </View>
        </View>

        {/* Request Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Request Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>MR Number</Text>
            <Text style={styles.value}>{request?.mr_number || request?.code || "N/A"}</Text>
          </View>
          {request?.created_on && (
            <View style={styles.row}>
              <Text style={styles.label}>Raised on</Text>
              <Text style={styles.value}>{formatDate(request.created_on)}</Text>
            </View>
          )}
          {request?.expectedBy && (
            <View style={styles.row}>
              <Text style={styles.label}>Delivery expected by</Text>
              <Text style={styles.value}>{formatDate(request.expectedBy)}</Text>
            </View>
          )}

          {request?.site_name && (
            <View style={styles.row}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{request.site_name}</Text>
            </View>
          )}
          {request?.raisedBy && (
            <View style={styles.row}>
              <Text style={styles.label}>Raised by</Text>
              <Text style={styles.value}>{request.raisedBy}</Text>
            </View>
          )}
          {typeof request?.items === "number" && (
            <View style={styles.row}>
              <Text style={styles.label}>Total items requested</Text>
              <Text style={styles.value}>{request.items}</Text>
            </View>
          )}
        </View>

        {/* Material List */}
        {safeLines.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Requested Materials</Text>
            <View style={styles.materialsTable}>
              {/* Header */}
              <View style={[styles.itemRow, styles.tableHeader]}>
                <Text style={[styles.colMaterial, { fontWeight: "bold" }]}>
                  Material
                </Text>
                <Text style={[styles.colCategory, { fontWeight: "bold" }]}>
                  Category
                </Text>
                <Text style={[styles.colQty, { fontWeight: "bold" }]}>
                  Quantity
                </Text>
                <Text style={[styles.colUom, { fontWeight: "bold" }]}>UOM</Text>
              </View>

              {safeLines.map((line, idx) => (
                <View style={styles.itemRow} key={line.id || idx}>
                  <Text style={styles.colMaterial}>
                    {line.material_name || "N/A"}
                  </Text>
                  <Text style={styles.colCategory}>{line.subcategory || "N/A"}</Text>
                  <Text style={styles.colQty}>
                    {line.quantity}
                  </Text>
                  <Text style={styles.colUom}>{formatUom(line.uom) || "N/A"}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer note */}
        <View style={styles.footer}>
          <Text>
            This receipt captures the material request details for record-keeping and
            sharing.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const PurchaseOrderReceiptPDF = ({ po, poCode, vendor }) => {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const purchaseAmount = po?.lines?.reduce(
    (total, item) => total + item.unit_price * item.quantity,
    0
  ) || 0;

  const gstAmount = po?.lines?.reduce(
    (total, item) => total + item.tax_amount,
    0
  ) || 0;

  const grandTotal = purchaseAmount + gstAmount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <OrgHeader />

        {/* Header */}
        <View style={styles.docHeader}>
          <View>
            <Text style={styles.docTitle}>Purchase Order</Text>
            <Text style={styles.metaText}>PO No: {poCode || "N/A"}</Text>
            <Text style={styles.metaText}>Status: {po?.status?.toUpperCase() || "N/A"}</Text>
          </View>
          <View style={styles.docMeta}>
            <Text style={styles.metaText}>Date: {formatDate(po?.order_date)}</Text>
          </View>
        </View>

        {/* Vendor Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Vendor Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Vendor Name</Text>
            <Text style={styles.value}>{vendor || "N/A"}</Text>
          </View>
          {po?.site_name && (
            <View style={styles.row}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{po.site_name}</Text>
            </View>
          )}
          {!po?.site && (
            <View style={styles.row}>
              <Text style={styles.label}>Delivery Location</Text>
              <Text style={styles.value}>Warehouse</Text>
            </View>
          )}
        </View>

        {/* Material List */}
        {po?.lines?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Materials</Text>
            <View style={[styles.itemRow, styles.tableHeader]}>
              <Text style={[styles.colName, { fontWeight: "bold" }]}>
                Material Name
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                Qty
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                UOM
              </Text>
              <Text style={[styles.colRight, { fontWeight: "bold" }]}>
                Unit Price
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                GST
              </Text>
              <Text style={[styles.colRight, { fontWeight: "bold" }]}>
                Total
              </Text>
            </View>
            {po.lines.map((item, idx) => {
              const gstPercentage = item.unit_price * item.quantity > 0
                ? ((item.tax_amount / (item.unit_price * item.quantity)) * 100).toFixed(2)
                : 0;

              return (
                <View style={styles.itemRow} key={idx}>
                  <Text style={styles.colName}>
                    {item.material_name || "N/A"}
                  </Text>
                  <Text style={styles.colNormal}>{item.quantity}</Text>
                  <Text style={styles.colNormal}>{item.uom || "N/A"}</Text>
                  <Text style={styles.colRight}>
                    {item.unit_price?.toLocaleString("en-IN") || "0"}
                  </Text>
                  <Text style={styles.colNormal}>{gstPercentage}%</Text>
                  <Text style={styles.colRight}>
                    {item.total_price?.toLocaleString("en-IN") || "0"}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Payment Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Summary</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Purchase Amount</Text>
            <Text style={styles.summaryValue}>
              Rs. {purchaseAmount.toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>GST Amount</Text>
            <Text style={styles.summaryValue}>
              Rs. {gstAmount.toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Grand Total</Text>
            <Text style={styles.totalValue}>
              Rs. {grandTotal.toLocaleString("en-IN")}
            </Text>
          </View>
        </View>

        {/* Payment Method */}
        {po?.payment_method && (
          <View style={styles.section}>
            <View style={styles.row}>
              <Text style={styles.label}>Payment Method</Text>
              <Text style={styles.value}>{po.payment_method}</Text>
            </View>
          </View>
        )}

        <View style={styles.footer}>
          <Text>
            This is a computer-generated receipt and does not require signature.
          </Text>
          <Text>For any queries, please contact support.</Text>
        </View>
      </Page>
    </Document>
  );
};

/**
 * Quotation receipt: simpler PDF listing material names, quantities and tags.
 * Designed to be downloaded from the Quotation Summary UI.
 */
export const QuotationReceiptPDF = ({ rfq }) => {
  const safeLines = Array.isArray(rfq?.lines) ? rfq.lines : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <OrgHeader />

        <View style={styles.docHeader}>
          <View>
            <Text style={styles.docTitle}>Quotation Receipt</Text>
            <Text style={styles.metaText}>
              RFQ: {rfq?.rfq_number || rfq?.id || "N/A"}
            </Text>
            {rfq?.material_request_number && (
              <Text style={styles.metaText}>
                MR: {rfq.material_request_number}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Content</Text>
          <View style={styles.materialsTable}>
            <View style={[styles.itemRow, styles.tableHeader]}>
              <Text style={[styles.colName, { fontWeight: "bold" }]}>
                Material
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                Quantity
              </Text>
              <Text style={[styles.colName, { fontWeight: "bold" }]}>Tags</Text>
            </View>

            {safeLines.map((line, idx) => (
              <View style={styles.itemRow} key={line.id || idx}>
                <Text style={styles.colName}>
                  {line.material_name || "N/A"}
                </Text>
                <Text style={styles.colNormal}>
                  {line.quantity ?? "-"} {line.uom || ""}
                </Text>
                <Text style={styles.colName}>
                  {Array.isArray(line.tags)
                    ? line.tags.join(", ")
                    : line.tags || "—"}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.footer}>
          <Text>
            This receipt lists the materials in the RFQ together with requested
            quantities and any associated tags.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PurchaseOrderReceiptPDF;